import { useState, useEffect } from "react";
import { Activity, Copy, Check, Power, PowerOff, ExternalLink, RefreshCw, Shield } from "lucide-react";
import getApiUrl from '../../utils/getApiUrl';

const API = getApiUrl();

export default function DiagnosticPanel({ token }) {
  const [config, setConfig] = useState({ enabled: false, token: null, url: null });
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [copied, setCopied] = useState(false);
  const [diagResult, setDiagResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const fetchStatus = async () => {
    try {
      const res = await fetch(`${API}/api/diag/admin/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setConfig(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchStatus(); }, []);

  const toggle = async (enabled) => {
    setToggling(true);
    try {
      const res = await fetch(`${API}/api/diag/admin/toggle`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ enabled }),
      });
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
        if (!enabled) setDiagResult(null);
      }
    } catch (e) { console.error(e); }
    setToggling(false);
  };

  const copyUrl = () => {
    if (config.url) {
      navigator.clipboard.writeText(config.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const runTest = async () => {
    if (!config.token) return;
    setTesting(true);
    setDiagResult(null);
    try {
      const res = await fetch(`${API}/api/diag/${config.token}`);
      if (res.ok) setDiagResult(await res.json());
      else setDiagResult({ error: `HTTP ${res.status}` });
    } catch (e) {
      setDiagResult({ error: e.message });
    }
    setTesting(false);
  };

  if (loading) return <div className="text-slate-400 text-sm p-4">Chargement...</div>;

  const statusColor = (s) => {
    if (s === "ok" || s === "reachable") return "text-green-400";
    if (s === "not_found" || s === "error" || s === "unreachable") return "text-red-400";
    return "text-amber-400";
  };

  const statusBadge = (s) => {
    const colors = {
      ok: "bg-green-500/20 text-green-400 border-green-500/30",
      reachable: "bg-green-500/20 text-green-400 border-green-500/30",
      error: "bg-red-500/20 text-red-400 border-red-500/30",
      not_found: "bg-red-500/20 text-red-400 border-red-500/30",
      unreachable: "bg-red-500/20 text-red-400 border-red-500/30",
      not_configured: "bg-amber-500/20 text-amber-400 border-amber-500/30",
    };
    return colors[s] || "bg-slate-500/20 text-slate-400 border-slate-500/30";
  };

  return (
    <div className="space-y-6" data-testid="diagnostic-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
            <Activity className="w-5 h-5 text-cyan-400" />
            Diagnostic Distant
          </h3>
          <p className="text-sm text-slate-400 mt-1">
            Active un lien temporaire pour diagnostiquer la plateforme a distance (sans authentification).
          </p>
        </div>
      </div>

      {/* Toggle card */}
      <div className={`rounded-xl border p-5 transition-all ${config.enabled ? "bg-cyan-950/30 border-cyan-500/40" : "bg-slate-800/50 border-slate-700/50"}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${config.enabled ? "bg-cyan-400 animate-pulse" : "bg-slate-600"}`} />
            <span className="font-medium text-slate-200">
              {config.enabled ? "Diagnostic actif" : "Diagnostic desactive"}
            </span>
          </div>
          <button
            data-testid="diag-toggle-btn"
            onClick={() => toggle(!config.enabled)}
            disabled={toggling}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
              config.enabled
                ? "bg-red-600/20 border border-red-500/40 text-red-300 hover:bg-red-600/30"
                : "bg-cyan-600/20 border border-cyan-500/40 text-cyan-300 hover:bg-cyan-600/30"
            }`}
          >
            {config.enabled ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
            {toggling ? "..." : config.enabled ? "Desactiver" : "Activer"}
          </button>
        </div>

        {config.enabled && config.url && (
          <div className="space-y-3">
            <div className="bg-slate-900/80 rounded-lg p-3 flex items-center gap-2">
              <code className="text-xs text-cyan-300 flex-1 break-all font-mono" data-testid="diag-url">
                {config.url}
              </code>
              <button
                data-testid="diag-copy-btn"
                onClick={copyUrl}
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors shrink-0"
                title="Copier"
              >
                {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-slate-400" />}
              </button>
              <a
                href={config.url}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors shrink-0"
                title="Ouvrir"
                data-testid="diag-open-btn"
              >
                <ExternalLink className="w-4 h-4 text-slate-400" />
              </a>
            </div>

            <div className="flex items-center gap-2">
              <button
                data-testid="diag-test-btn"
                onClick={runTest}
                disabled={testing}
                className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 rounded-lg text-sm font-medium transition-colors"
              >
                <RefreshCw className={`w-4 h-4 ${testing ? "animate-spin" : ""}`} />
                {testing ? "Test en cours..." : "Tester maintenant"}
              </button>
              <div className="flex items-center gap-1 text-xs text-slate-500">
                <Shield className="w-3 h-3" />
                Protege par token unique - fermez apres utilisation
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Results */}
      {diagResult && !diagResult.error && (
        <div className="space-y-4" data-testid="diag-results">
          <div className="text-xs text-slate-500 font-mono">
            Diagnostic execute le {new Date(diagResult.timestamp).toLocaleString("fr-FR")} — Domaine: {diagResult.domain}
          </div>

          {/* MongoDB */}
          {diagResult.checks?.mongodb && (
            <div className="rounded-xl border border-slate-700/50 bg-slate-800/40 p-4">
              <h4 className="font-semibold text-sm text-slate-200 mb-2">Base de donnees (MongoDB)</h4>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 text-xs rounded border ${statusBadge(diagResult.checks.mongodb.status)}`}>
                  {diagResult.checks.mongodb.status}
                </span>
                {diagResult.checks.mongodb.collections && (
                  <span className="text-xs text-slate-400">{diagResult.checks.mongodb.collections} collections</span>
                )}
              </div>
            </div>
          )}

          {/* Webhooks */}
          {diagResult.checks?.webhooks && (
            <div className="rounded-xl border border-slate-700/50 bg-slate-800/40 p-4">
              <h4 className="font-semibold text-sm text-slate-200 mb-3">Webhooks ({diagResult.checks.webhooks.length} endpoints)</h4>
              <div className="space-y-2">
                {diagResult.checks.webhooks.map((wh, i) => (
                  <div key={i} className="flex items-center justify-between text-sm bg-slate-900/50 rounded-lg px-3 py-2">
                    <div>
                      <span className="text-slate-200 font-medium">{wh.name}</span>
                      <span className="text-slate-500 text-xs ml-2">({wh.service})</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="text-xs text-slate-500 hidden lg:block">{wh.http_code || "-"}</code>
                      <span className={`px-2 py-0.5 text-xs rounded border ${statusBadge(wh.status)}`}>
                        {wh.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* OAuth Callbacks */}
          {diagResult.checks?.oauth_callbacks && (
            <div className="rounded-xl border border-slate-700/50 bg-slate-800/40 p-4">
              <h4 className="font-semibold text-sm text-slate-200 mb-3">OAuth Callbacks</h4>
              <div className="space-y-2">
                {diagResult.checks.oauth_callbacks.map((cb, i) => (
                  <div key={i} className="flex items-center justify-between text-sm bg-slate-900/50 rounded-lg px-3 py-2">
                    <div>
                      <span className="text-slate-200 font-medium">{cb.name}</span>
                      <span className="text-slate-500 text-xs ml-2">({cb.service})</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {cb.env_value && <code className="text-xs text-slate-500 hidden lg:block truncate max-w-xs">{cb.env_value}</code>}
                      <span className={`px-2 py-0.5 text-xs rounded border ${cb.env_configured ? "bg-green-500/20 text-green-400 border-green-500/30" : "bg-red-500/20 text-red-400 border-red-500/30"}`}>
                        {cb.env_configured ? "configure" : "manquant"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Environment */}
          {diagResult.checks?.environment && (
            <div className="rounded-xl border border-slate-700/50 bg-slate-800/40 p-4">
              <h4 className="font-semibold text-sm text-slate-200 mb-3">Variables d'environnement</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {Object.entries(diagResult.checks.environment).map(([key, val]) => (
                  <div key={key} className="flex items-center justify-between text-xs bg-slate-900/50 rounded-lg px-3 py-2">
                    <code className="text-slate-300">{key}</code>
                    <span className={`px-2 py-0.5 rounded border ${val.configured ? "bg-green-500/20 text-green-400 border-green-500/30" : "bg-red-500/20 text-red-400 border-red-500/30"}`}>
                      {val.preview}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Ringover */}
          {diagResult.checks?.ringover && (
            <div className="rounded-xl border border-slate-700/50 bg-slate-800/40 p-4">
              <h4 className="font-semibold text-sm text-slate-200 mb-2">Ringover</h4>
              <span className={`px-2 py-0.5 text-xs rounded border ${statusBadge(diagResult.checks.ringover.status)}`}>
                {diagResult.checks.ringover.status}
              </span>
            </div>
          )}

          {/* Activity & Data */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {diagResult.checks?.activity_24h && (
              <div className="rounded-xl border border-slate-700/50 bg-slate-800/40 p-4">
                <h4 className="font-semibold text-sm text-slate-200 mb-2">Activite (24h)</h4>
                <div className="flex gap-4 text-sm">
                  <span className="text-slate-300">{diagResult.checks.activity_24h.leads ?? 0} leads</span>
                  <span className="text-slate-300">{diagResult.checks.activity_24h.sessions ?? 0} sessions</span>
                </div>
              </div>
            )}
            {diagResult.checks?.data && (
              <div className="rounded-xl border border-slate-700/50 bg-slate-800/40 p-4">
                <h4 className="font-semibold text-sm text-slate-200 mb-2">Donnees</h4>
                <div className="flex gap-4 text-sm">
                  <span className="text-slate-300">{diagResult.checks.data.tenants ?? 0} tenants</span>
                  <span className="text-slate-300">{diagResult.checks.data.users ?? 0} utilisateurs</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {diagResult?.error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-sm text-red-300" data-testid="diag-error">
          Erreur: {diagResult.error}
        </div>
      )}
    </div>
  );
}
